import { Link } from "react-router-dom";
import { Sticker } from "./Sticker";
import { audienceLabel, money } from "../lib/format";
import { isNoCostOrigin, type ItemSummary } from "../lib/api";

/**
 * An inventory row rendered as the thing it represents: a hang tag. The swatch
 * on the left is the SKU, not a photo, so a hundred rows cost zero image
 * requests and the list stays instant on a phone in a store aisle.
 *
 * Size and audience are the two things you are asked for first when someone
 * is browsing, so they sit apart from the meta row: size as its own pill on
 * the price side (shown even when blank, so a gap is visible and gets fixed),
 * audience as a chip.
 */
export function TagCard({ item }: { item: ItemSummary }) {
  const audience = audienceLabel(item.audience);
  const fromCloset = isNoCostOrigin(item.store);
  return (
    <Link to={`/items/${item.id}`} className="tag">
      {item.thumbUrl ? (
        <span className="tag-thumb tag-thumb-img">
          <img src={item.thumbUrl} alt="" loading="lazy" />
        </span>
      ) : (
        <span className="tag-thumb" aria-hidden="true" style={swatchStyle(item.status)}>
          <span className="num" style={{ fontSize: "0.625rem", opacity: 0.75 }}>
            {item.sku.replace("T-", "")}
          </span>
        </span>
      )}

      <span className="tag-body">
        <span className="tag-title">{item.title ?? "Untitled find"}</span>
        <span className="tag-meta">
          <Sticker status={item.status} />
          <span className="num">{item.sku}</span>
          {item.isPersonal && <span className="personal-flag">personal</span>}
          {fromCloset && <span className="personal-flag closet-flag">{item.store}</span>}
          {item.brand && <span>{item.brand}</span>}
          {audience && <span className="tag-chip">{audience}</span>}
          {item.category && <span>{item.category}</span>}
          {item.activeListings > 0 && (
            <span className="num">{item.activeListings} live</span>
          )}
        </span>
      </span>

      <span className="tag-price">
        <span className={`tag-size ${item.sizeLabel ? "" : "blank"}`}>
          {item.sizeLabel ? item.sizeLabel : "size —"}
        </span>
        <span className="paid">
          {fromCloset ? "no cost" : `paid ${money(item.purchasePrice)}`}
        </span>
      </span>
    </Link>
  );
}

const SWATCH: Record<string, string> = {
  draft: "var(--tag-draft)",
  in_stock: "var(--tag-stock)",
  listed: "var(--tag-listed)",
  sold: "var(--tag-sold)",
  shipped: "var(--tag-shipped)",
  damaged: "var(--tag-alert)",
};

function swatchStyle(status: string): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "0.25rem",
    background: `color-mix(in srgb, ${SWATCH[status] ?? "var(--tag-draft)"} 22%, var(--card))`,
    borderColor: SWATCH[status] ?? "var(--rule)",
    color: "var(--ink)",
  };
}
